import { useCallback, useState } from "react";

import { friendlyError } from "../data/callables";
import { normalizeApplicationError } from "../shared/errors";
import type { ApplicationErrorCategory } from "../shared/errors";

interface CommandRunnerOptions {
  showToast: (message: string) => void;
}

export interface CommandRunner {
  run: <T>(
    operation: () => Promise<T>,
    onSuccess?: (value: T) => void,
  ) => Promise<boolean>;
  onError: (error: unknown) => void;
  pendingCount: number;
  lastErrorCategory:
    ReturnType<typeof normalizeApplicationError>["category"] | null;
}

export class OutcomeUnknownError extends Error {
  readonly cause: unknown;

  constructor(cause: unknown) {
    super("The connection was interrupted after the request was sent.");
    this.name = "OutcomeUnknownError";
    this.cause = cause;
  }
}

export function isOutcomeUnknownError(
  error: unknown,
): error is OutcomeUnknownError {
  return error instanceof OutcomeUnknownError;
}

export function useCommandRunner({
  showToast,
}: CommandRunnerOptions): CommandRunner {
  const [pendingCount, setPendingCount] = useState(0);
  const [lastErrorCategory, setLastErrorCategory] =
    useState<ApplicationErrorCategory | null>(null);
  const onError = useCallback(
    (error: unknown) => {
      const normalized = normalizeApplicationError(error, friendlyError(error));
      setLastErrorCategory(normalized.category);
      showToast(normalized.message);
    },
    [showToast],
  );
  const run = useCallback(
    <T>(operation: () => Promise<T>, onSuccess?: (value: T) => void) => {
      setPendingCount((count) => count + 1);
      return operation()
        .then((value) => {
          setLastErrorCategory(null);
          onSuccess?.(value);
          return true;
        })
        .catch((error) => {
          const normalized = normalizeApplicationError(
            error,
            friendlyError(error),
          );
          if (normalized.category === "offline") {
            setLastErrorCategory("offline");
            showToast("Connection interrupted — checking whether it saved");
            throw new OutcomeUnknownError(error);
          }
          onError(error);
          return false;
        })
        .finally(() => setPendingCount((count) => Math.max(0, count - 1)));
    },
    [onError, showToast],
  );

  return { run, onError, pendingCount, lastErrorCategory };
}
